import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { addArticle, updateArticle, getArticleById, getCategories } from '../../firebase/firestore';
import { uploadImage } from '../../firebase/storage';
import './AddEditArticle.css';

const DEFAULT_CATS = ['Politics', 'Sports', 'Technology', 'Entertainment', 'Business', 'World', 'Health', 'Education'];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean'],
  ],
};

const LANG_CONFIG = [
  { code: 'english', label: 'English', flag: '🇬🇧', native: 'English', placeholder: { title: 'Enter headline in English...', content: 'Write article content in English...' } },
  { code: 'telugu', label: 'Telugu', flag: '🇮🇳', native: 'తెలుగు', placeholder: { title: 'తెలుగులో శీర్షిక రాయండి...', content: 'తెలుగులో వ్యాసం రాయండి...' } },
  { code: 'hindi', label: 'Hindi', flag: '🇮🇳', native: 'हिन्दी', placeholder: { title: 'हिन्दी में शीर्षक लिखें...', content: 'हिन्दी में लेख लिखें...' } },
];

const emptyLang = () => ({ title: '', content: '' });

export default function AddEditArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Shared fields
  const [shared, setShared] = useState({
    category: '', author: '', tags: '', status: 'published'
  });

  // Per-language fields: { english: {title, content}, telugu: {title, content}, hindi: {title, content} }
  const [langs, setLangs] = useState({
    english: emptyLang(),
    telugu: emptyLang(),
    hindi: emptyLang(),
  });

  const [activeTab, setActiveTab] = useState('english');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCategories().then(cats => {
      setCategories(cats.length ? cats.map(c => c.name) : DEFAULT_CATS);
    });
    if (isEdit) {
      setLoading(true);
      getArticleById(id).then(article => {
        if (article) {
          setShared({
            category: article.category || '',
            author: article.author || '',
            tags: article.tags || '',
            status: article.status || 'published',
          });

          // Support both old single-language format and new multi-language format
          if (article.translations) {
            setLangs({
              english: article.translations.english || emptyLang(),
              telugu: article.translations.telugu || emptyLang(),
              hindi: article.translations.hindi || emptyLang(),
            });
          } else {
            // Migrate old single-language article
            const langCode = article.language || 'english';
            setLangs(prev => ({
              ...prev,
              [langCode]: { title: article.title || '', content: article.content || '' }
            }));
          }

          if (article.thumbnailURL) setThumbnailPreview(article.thumbnailURL);
        }
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const handleSharedChange = (e) => {
    const { name, value } = e.target;
    setShared(s => ({ ...s, [name]: value }));
  };

  const handleLangField = (langCode, field, value) => {
    setLangs(prev => ({
      ...prev,
      [langCode]: { ...prev[langCode], [field]: value }
    }));
  };

  const handleThumbnail = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  // Count how many languages have content
  const filledLanguages = LANG_CONFIG.filter(l => langs[l.code].title.trim() && langs[l.code].content.trim());

  const handleSubmit = async (statusOverride) => {
    if (!shared.category) return setError('Please select a category');
    if (!shared.author.trim()) return setError('Author name is required');
    if (filledLanguages.length === 0) return setError('Please fill at least one language (title + content)');

    setSaving(true);
    setError('');

    try {
      let thumbnailURL = thumbnailPreview || '';
      if (thumbnailFile) {
        thumbnailURL = await uploadImage(thumbnailFile, 'thumbnails');
      }

      const status = statusOverride || shared.status;

      // Build translations object — only include languages that have both title & content
      const translations = {};
      LANG_CONFIG.forEach(({ code }) => {
        const t = langs[code];
        if (t.title.trim() && t.content.trim()) {
          translations[code] = { title: t.title.trim(), content: t.content.trim() };
        }
      });

      // For backwards compatibility: use first filled language as top-level title/content
      const primary = translations[filledLanguages[0].code];

      const data = {
        ...shared,
        status,
        thumbnailURL,
        translations,
        // Top-level fields for backwards compatibility with public pages
        title: primary.title,
        content: primary.content,
        language: filledLanguages.map(l => l.code).join(','),
        languages: filledLanguages.map(l => l.code),
      };

      if (isEdit) {
        await updateArticle(id, data);
      } else {
        await addArticle(data);
      }

      navigate('/admin/manage-articles');
    } catch (err) {
      setError('Failed to save article. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-spinner">Loading article...</div>;

  return (
    <div className="add-article">
      <div className="page-header-row">
        <h1 className="page-title">{isEdit ? '✏️ Edit Article' : '✏️ Write New Article'}</h1>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/manage-articles')}>← Back</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="article-form">
        {/* ── Shared Fields ── */}
        <div className="form-section">
          <h3 className="form-section-title">📋 Article Details</h3>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Author Name *</label>
              <input name="author" value={shared.author} onChange={handleSharedChange} placeholder="Reporter / Staff Writer" required />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select name="category" value={shared.category} onChange={handleSharedChange} required>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input name="tags" value={shared.tags} onChange={handleSharedChange} placeholder="breaking, local, economy" />
            </div>
            <div className="form-group">
              <label>Publish Status</label>
              <select name="status" value={shared.status} onChange={handleSharedChange}>
                <option value="published">✅ Published (Live)</option>
                <option value="draft">📝 Draft (Hidden)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Thumbnail Image</label>
            <div className="thumbnail-uploader">
              <input type="file" accept="image/*" onChange={handleThumbnail} id="thumb-input" style={{ display: 'none' }} />
              <label htmlFor="thumb-input" className="thumb-upload-btn">📷 Choose Image</label>
              {thumbnailPreview && (
                <div className="thumb-preview">
                  <img src={thumbnailPreview} alt="Preview" />
                  <button type="button" className="remove-thumb" onClick={() => { setThumbnailPreview(''); setThumbnailFile(null); }}>✕</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Language Tabs ── */}
        <div className="form-section">
          <div className="lang-section-header">
            <h3 className="form-section-title">🌐 Content by Language</h3>
            <div className="lang-fill-badges">
              {LANG_CONFIG.map(({ code, flag, label }) => {
                const filled = langs[code].title.trim() && langs[code].content.trim();
                return (
                  <span key={code} className={`lang-fill-badge ${filled ? 'filled' : 'empty'}`}>
                    {flag} {label} {filled ? '✓' : '–'}
                  </span>
                );
              })}
            </div>
          </div>

          <p className="lang-hint">
            Fill any languages you want to publish. Articles will be posted only for filled languages.
          </p>

          {/* Tab headers */}
          <div className="lang-tabs-nav">
            {LANG_CONFIG.map(({ code, flag, label, native }) => {
              const filled = langs[code].title.trim() && langs[code].content.trim();
              return (
                <button
                  key={code}
                  type="button"
                  className={`lang-tab-btn ${activeTab === code ? 'active' : ''} ${filled ? 'has-content' : ''}`}
                  onClick={() => setActiveTab(code)}
                >
                  <span className="tab-flag">{flag}</span>
                  <span className="tab-labels">
                    <span className="tab-label">{label}</span>
                    <span className="tab-native">{native}</span>
                  </span>
                  {filled && <span className="tab-check">✓</span>}
                </button>
              );
            })}
          </div>

          {/* Tab panels */}
          {LANG_CONFIG.map(({ code, placeholder }) => (
            <div key={code} className={`lang-tab-panel ${activeTab === code ? 'active' : ''}`}>
              <div className="form-group">
                <label>
                  Headline / Title
                  <span className="optional-label">optional if not posting in this language</span>
                </label>
                <input
                  value={langs[code].title}
                  onChange={e => handleLangField(code, 'title', e.target.value)}
                  placeholder={placeholder.title}
                />
              </div>
              <div className="form-group">
                <label>Article Content</label>
                <div className="quill-wrapper">
                  <ReactQuill
                    value={langs[code].content}
                    onChange={val => handleLangField(code, 'content', val)}
                    modules={QUILL_MODULES}
                    placeholder={placeholder.content}
                    theme="snow"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Actions ── */}
        <div className="form-actions">
          <div className="lang-post-summary">
            {filledLanguages.length > 0
              ? <span className="will-post">Will post in: {filledLanguages.map(l => `${l.flag} ${l.label}`).join(' · ')}</span>
              : <span className="wont-post">Fill at least one language to publish</span>
            }
          </div>
          <div className="action-btns">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/manage-articles')}>Cancel</button>
            <button
              type="button"
              className="btn btn-dark"
              onClick={() => handleSubmit('draft')}
              disabled={saving}
            >
              💾 Save as Draft
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleSubmit(null)}
              disabled={saving}
            >
              {saving ? 'Saving...' : isEdit ? '✅ Update Article' : '🚀 Publish Article'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}